<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { extractServerError } from 'dashboard/helper/appointmentFormErrors';
import ProfessionalForm from './ProfessionalForm.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

const searchQuery = ref('');
const showForm = ref(false);
const showDeleteConfirm = ref(false);
const selectedProfessional = ref(null);
// AP-03: backend validation message shown inside the open form on save failure.
const formError = ref('');
const formAccountContext = ref(null);
const deleteAccountContext = ref(null);
let accountGeneration = 0;

const currentAccountId = computed(() => {
  const getter = store.getters.getCurrentAccountId;
  return typeof getter === 'function' ? getter() : getter;
});

const normalizeAccountId = value => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const captureAccountContext = (accountId = currentAccountId.value) => ({
  accountId: normalizeAccountId(accountId),
  generation: accountGeneration,
});

const isCurrentAccountContext = context =>
  Boolean(context?.accountId) &&
  context.accountId === normalizeAccountId(currentAccountId.value) &&
  context.generation === accountGeneration;

const professionals = computed(
  () => store.getters['professionals/getProfessionals']
);
const uiFlags = computed(() => store.getters['professionals/getUIFlags']);

// ─── O que um profissional É nesta ferramenta ────────────────────────────────
// O card mostrava nome, especialidade e e-mail, e sobravam ~120px de vazio. As
// duas perguntas que se faz sobre um profissional de agenda — o que ele faz e
// quando ele trabalha — não tinham resposta na tela, embora a API já mandasse
// working_hours e service_ids desde sempre.
const appointmentServices = computed(
  () => store.getters['appointmentServices/getAppointmentServices'] ?? []
);

const serviceNamesById = computed(() => {
  const map = new Map();
  appointmentServices.value.forEach(service =>
    map.set(service.id, service.name)
  );
  return map;
});

function servicesOf(professional) {
  return (professional.service_ids ?? [])
    .map(id => serviceNamesById.value.get(id))
    .filter(Boolean);
}

// Semana começando na segunda, como a agenda. Chaves explícitas: montar
// `WEEK.${dia}` esconde a string do extrator de i18n.
const weekDays = computed(() => [
  { key: 'mon', label: t('APPOINTMENTS.PROFESSIONALS.WEEK.MON') },
  { key: 'tue', label: t('APPOINTMENTS.PROFESSIONALS.WEEK.TUE') },
  { key: 'wed', label: t('APPOINTMENTS.PROFESSIONALS.WEEK.WED') },
  { key: 'thu', label: t('APPOINTMENTS.PROFESSIONALS.WEEK.THU') },
  { key: 'fri', label: t('APPOINTMENTS.PROFESSIONALS.WEEK.FRI') },
  { key: 'sat', label: t('APPOINTMENTS.PROFESSIONALS.WEEK.SAT') },
  { key: 'sun', label: t('APPOINTMENTS.PROFESSIONALS.WEEK.SUN') },
]);

function worksOn(professional, dayKey) {
  return Boolean(professional.working_hours?.[dayKey]?.length);
}

// Faixa do dia: o menor início e o maior fim entre todos os dias trabalhados.
// Um profissional com horários diferentes por dia vê a envoltória, não uma
// média — a agenda continua sendo a fonte exata.
function workingHoursLabel(professional) {
  const windows = Object.values(professional.working_hours ?? {}).flat();
  const starts = windows
    .map(w => w.start)
    .filter(Boolean)
    .sort();
  const ends = windows
    .map(w => w.end)
    .filter(Boolean)
    .sort();
  if (!starts.length || !ends.length) return null;

  const range = `${starts[0]}–${ends.at(-1)}`;
  if (!professional.buffer_minutes) return range;
  return `${range} · ${t('APPOINTMENTS.PROFESSIONALS.BUFFER_SHORT', {
    minutes: professional.buffer_minutes,
  })}`;
}

const filteredProfessionals = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return professionals.value;
  return professionals.value.filter(
    p =>
      p.name?.toLowerCase().includes(q) ||
      p.specialty?.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q)
  );
});

// O card nomeia os serviços do profissional, e o payload traz só os ids. Nada
// aqui pode derrubar a lista de profissionais: sem catálogo, o card mostra o
// que sabe e segue. Vuex devolve `undefined` para ação de módulo ausente, então
// não dá para encadear .catch direto no retorno.
const fetchServiceCatalog = async context => {
  try {
    await Promise.resolve(store.dispatch('appointmentServices/get', context));
  } catch {
    // catálogo indisponível — sem serviço nomeado no card
  }
};

const fetchProfessionals = async context => {
  const requestContext = context || captureAccountContext();
  if (!isCurrentAccountContext(requestContext)) return [];

  try {
    return await store.dispatch('professionals/get', requestContext);
  } catch {
    return [];
  }
};

const openCreateForm = () => {
  const context = captureAccountContext();
  if (!isCurrentAccountContext(context)) return;

  selectedProfessional.value = null;
  formError.value = '';
  formAccountContext.value = context;
  showForm.value = true;
};

const openEditForm = professional => {
  const context = captureAccountContext(
    professional.account_id ?? currentAccountId.value
  );
  if (!isCurrentAccountContext(context)) return;

  selectedProfessional.value = professional;
  formError.value = '';
  formAccountContext.value = context;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  selectedProfessional.value = null;
  formError.value = '';
  formAccountContext.value = null;
};

const confirmDelete = professional => {
  const context = captureAccountContext(
    professional.account_id ?? currentAccountId.value
  );
  if (!isCurrentAccountContext(context)) return;

  selectedProfessional.value = professional;
  deleteAccountContext.value = context;
  showDeleteConfirm.value = true;
};

const closeDeleteConfirm = context => {
  if (context && deleteAccountContext.value !== context) return;

  showDeleteConfirm.value = false;
  selectedProfessional.value = null;
  deleteAccountContext.value = null;
};

const resetAccountOwnedUI = () => {
  searchQuery.value = '';
  showForm.value = false;
  showDeleteConfirm.value = false;
  selectedProfessional.value = null;
  formError.value = '';
  formAccountContext.value = null;
  deleteAccountContext.value = null;
};

const handleFormSubmit = async data => {
  const requestContext = formAccountContext.value;
  if (!isCurrentAccountContext(requestContext)) return;

  const professionalId = selectedProfessional.value?.id;
  const successMessage = professionalId
    ? t('APPOINTMENTS.PROFESSIONALS.UPDATED')
    : t('APPOINTMENTS.PROFESSIONALS.CREATED');
  formError.value = '';
  try {
    let result;
    if (professionalId) {
      result = await store.dispatch('professionals/update', {
        ...data,
        id: professionalId,
        ...requestContext,
      });
    } else {
      result = await store.dispatch('professionals/create', {
        ...data,
        ...requestContext,
      });
    }

    if (
      !result ||
      formAccountContext.value !== requestContext ||
      !isCurrentAccountContext(requestContext)
    ) {
      return;
    }

    showAlert(successMessage);
    closeForm();
  } catch (error) {
    if (
      formAccountContext.value !== requestContext ||
      !isCurrentAccountContext(requestContext)
    ) {
      return;
    }

    // AP-03: surface the backend validation message inside the open form.
    formError.value = extractServerError(error) || t('APPOINTMENTS.ERROR');
    showAlert(formError.value);
  }
};

const handleDelete = async () => {
  const requestContext = deleteAccountContext.value;
  const professionalId = selectedProfessional.value?.id;
  if (!professionalId || !isCurrentAccountContext(requestContext)) return;

  try {
    const deleted = await store.dispatch('professionals/delete', {
      id: professionalId,
      ...requestContext,
    });
    if (
      deleted !== true ||
      deleteAccountContext.value !== requestContext ||
      !isCurrentAccountContext(requestContext)
    ) {
      return;
    }

    showAlert(t('APPOINTMENTS.PROFESSIONALS.DELETED'));
  } catch {
    if (
      deleteAccountContext.value === requestContext &&
      isCurrentAccountContext(requestContext)
    ) {
      showAlert(t('APPOINTMENTS.ERROR'));
    }
  } finally {
    closeDeleteConfirm(requestContext);
  }
};

watch(
  currentAccountId,
  async accountId => {
    accountGeneration += 1;
    resetAccountOwnedUI();

    const pendingContext = {
      accountId: normalizeAccountId(accountId),
      generation: accountGeneration,
    };
    if (!pendingContext.accountId) return;

    // App.vue invalidates account-owned Vuex modules synchronously. Waiting one
    // tick ensures this fetch starts after that reset even if both watchers run
    // in the same route transition.
    await nextTick();
    if (
      pendingContext.accountId !== normalizeAccountId(currentAccountId.value) ||
      pendingContext.generation !== accountGeneration
    ) {
      return;
    }

    await fetchProfessionals(pendingContext);
    await fetchServiceCatalog(pendingContext);
  },
  { immediate: true, flush: 'sync' }
);
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden bg-n-background">
    <!-- Toolbar: search + CTA -->
    <div
      class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 px-6 lg:px-8 py-5 border-b border-n-weak/50 bg-n-alpha-1/10"
    >
      <div class="relative flex-1 max-w-md group">
        <span
          class="i-lucide-search absolute left-4 top-1/2 -translate-y-1/2 size-4.5 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
          aria-hidden="true"
        />
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="t('APPOINTMENTS.SEARCH_PLACEHOLDER')"
          :aria-label="t('APPOINTMENTS.SEARCH_PLACEHOLDER')"
          class="w-full border border-n-strong rounded-xl !pl-11 !pr-4 !mb-0 py-2.5 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:bg-n-background focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand placeholder-n-slate-9"
        />
      </div>
      <button
        type="button"
        class="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm hover:shadow-lg hover:shadow-n-brand/20 hover:-translate-y-px active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-brand whitespace-nowrap"
        @click="openCreateForm"
      >
        <span class="i-lucide-plus size-4.5" aria-hidden="true" />
        {{ t('APPOINTMENTS.PROFESSIONALS.ADD') }}
      </button>
    </div>

    <div
      class="flex-1 overflow-y-auto px-6 lg:px-8 py-6 relative custom-scrollbar"
    >
      <!-- Fetch error banner -->
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
      >
        <div
          v-if="uiFlags.isFetchError"
          class="rounded-xl border border-n-ruby-5 bg-gradient-to-r from-n-ruby-2 to-n-ruby-1 text-n-ruby-11 px-5 py-4 mb-5 text-sm flex items-center gap-3 shadow-sm"
          data-testid="fetch-error-banner"
        >
          <span class="i-lucide-alert-circle size-5" aria-hidden="true" />
          <span class="font-medium">{{ t('APPOINTMENTS.ERROR_FETCH') }}</span>
          <button
            type="button"
            class="ml-auto bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-12 px-4 py-1.5 rounded-lg font-semibold transition-colors"
            @click="fetchProfessionals()"
          >
            {{ t('APPOINTMENTS.RETRY') }}
          </button>
        </div>
      </Transition>

      <!-- Loading skeleton Premium -->
      <div
        v-if="uiFlags.isFetching"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        <div
          v-for="i in 4"
          :key="`skeleton-${i}`"
          class="h-32 rounded-[1.25rem] bg-gradient-to-r from-n-alpha-1 to-n-alpha-2 animate-pulse ring-1 ring-black/5"
        >
          <div class="p-5 flex flex-col gap-3 h-full">
            <div class="flex items-center gap-3">
              <div class="size-12 rounded-full bg-n-background/50" />
              <div class="space-y-2 flex-1">
                <div class="h-4 bg-n-background/50 rounded w-3/4" />
                <div class="h-3 bg-n-background/50 rounded w-1/2" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 translate-y-4"
      >
        <div
          v-if="!uiFlags.isFetching && filteredProfessionals.length === 0"
          class="flex flex-col items-center justify-center py-20 text-center rounded-[1.25rem] border border-dashed border-n-strong/50 bg-n-alpha-1/30"
        >
          <div
            class="size-20 rounded-3xl bg-n-alpha-2 flex items-center justify-center mb-5 ring-1 ring-white/10 shadow-inner"
          >
            <span
              class="i-lucide-user-cog size-10 text-n-slate-9"
              aria-hidden="true"
            />
          </div>
          <h3 class="text-lg font-bold text-n-slate-12 mb-2 tracking-tight">
            {{ t('APPOINTMENTS.PROFESSIONALS.EMPTY_STATE') }}
          </h3>
          <p
            class="text-sm font-medium text-n-slate-10 max-w-sm leading-relaxed mb-5"
          >
            {{
              searchQuery.trim()
                ? t(
                    'APPOINTMENTS.PROFESSIONALS.EMPTY_SEARCH_HINT',
                    'Tente outro termo de busca.'
                  )
                : t(
                    'APPOINTMENTS.PROFESSIONALS.EMPTY_HINT',
                    'Cadastre profissionais para começar a agendar atendimentos.'
                  )
            }}
          </p>
          <button
            v-if="!searchQuery.trim()"
            type="button"
            class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-n-brand"
            @click="openCreateForm"
          >
            <span class="i-lucide-plus size-4.5" aria-hidden="true" />
            {{ t('APPOINTMENTS.PROFESSIONALS.ADD') }}
          </button>
        </div>
      </Transition>

      <!-- Grid Cards Premium -->
      <div
        v-if="!uiFlags.isFetching && filteredProfessionals.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        <div
          v-for="professional in filteredProfessionals"
          :key="professional.id"
          class="group relative flex flex-col p-5 ps-6 rounded-[1.25rem] border border-n-weak bg-n-background hover:bg-n-alpha-1/20 transition-colors shadow-sm hover:shadow-md hover:border-n-brand/30 overflow-hidden"
        >
          <!-- A cor do profissional como faixa: antes era um ponto no avatar,
               que em vermelho lia como "inativo" ou "erro". Ela é a cor dele na
               agenda, não um estado. -->
          <span
            class="absolute inset-y-0 start-0 w-1"
            :style="{ backgroundColor: professional.color || '#3B82F6' }"
            aria-hidden="true"
          />

          <div class="flex items-start gap-4 mb-4 relative z-10">
            <Avatar
              :src="professional.avatar_url || ''"
              :name="professional.name || '?'"
              :size="44"
              rounded-full
            />
            <div class="flex-1 min-w-0 pt-1">
              <h3
                class="text-[15px] font-bold text-n-slate-12 leading-tight truncate group-hover:text-n-brand transition-colors"
              >
                {{ professional.name }}
              </h3>
              <p
                v-if="professional.specialty"
                class="text-xs font-semibold text-n-slate-10 uppercase tracking-wide mt-1 truncate"
              >
                {{ professional.specialty }}
              </p>
            </div>
          </div>

          <div class="flex-1 flex flex-col gap-3 relative z-10">
            <!-- Quando trabalha: tira da semana + envoltória do horário -->
            <div>
              <div class="flex items-center gap-1 mb-1.5">
                <!-- Abreviação inteira, não a inicial: em pt_BR segunda e
                     sábado começam com S, quarta e quinta com Q — a inicial só
                     se desfaz no hover, que não existe em toque. -->
                <span
                  v-for="day in weekDays"
                  :key="day.key"
                  class="px-1.5 h-5 rounded text-[10px] font-bold grid place-content-center"
                  :class="
                    worksOn(professional, day.key)
                      ? 'bg-n-brand/10 text-n-brand'
                      : 'bg-n-alpha-1 text-n-slate-9'
                  "
                >
                  {{ day.label }}
                </span>
              </div>
              <p
                v-if="workingHoursLabel(professional)"
                class="text-xs font-medium text-n-slate-11 tabular-nums"
              >
                {{ workingHoursLabel(professional) }}
              </p>
              <p v-else class="text-xs font-medium text-n-amber-11">
                {{ t('APPOINTMENTS.PROFESSIONALS.NO_WORKING_HOURS') }}
              </p>
            </div>

            <!-- O que faz: sem serviço vinculado, o profissional não é
                 agendável — a modal de agendamento recusa. Vale dizer aqui. -->
            <div>
              <div
                v-if="servicesOf(professional).length"
                class="flex flex-wrap gap-1"
              >
                <span
                  v-for="name in servicesOf(professional).slice(0, 3)"
                  :key="name"
                  class="px-2 py-0.5 rounded-md bg-n-alpha-1 text-[11px] font-medium text-n-slate-11 truncate max-w-[140px]"
                >
                  {{ name }}
                </span>
                <span
                  v-if="servicesOf(professional).length > 3"
                  class="px-2 py-0.5 rounded-md text-[11px] font-semibold text-n-slate-10 tabular-nums"
                >
                  {{
                    t('APPOINTMENTS.PROFESSIONALS.MORE_SERVICES', {
                      count: servicesOf(professional).length - 3,
                    })
                  }}
                </span>
              </div>
              <p
                v-else-if="!professional.service_ids?.length"
                class="text-xs font-medium text-n-amber-11"
              >
                {{ t('APPOINTMENTS.PROFESSIONALS.NO_SERVICES_LINKED') }}
              </p>
            </div>

            <div
              v-if="professional.email"
              class="flex items-center gap-2 text-xs font-medium text-n-slate-11 truncate"
            >
              <span
                class="i-lucide-mail size-3.5 flex-shrink-0 text-n-slate-10"
                aria-hidden="true"
              />
              <span class="truncate">{{ professional.email }}</span>
            </div>
          </div>

          <!-- Ações sempre visíveis: estavam atrás de opacity-0/hover, o que
               não existe em toque e depende de descoberta no desktop. -->
          <div
            class="flex items-center gap-2 pt-4 mt-4 border-t border-n-weak/50 relative z-10"
          >
            <button
              type="button"
              class="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors focus:outline-none focus:ring-2 focus:ring-n-slate-6"
              @click="openEditForm(professional)"
            >
              <span class="i-lucide-pencil size-3.5" aria-hidden="true" />
              {{ t('APPOINTMENTS.TABLE.EDIT') }}
            </button>
            <div class="w-px h-4 bg-n-weak/50" />
            <button
              type="button"
              class="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg text-n-ruby-10 hover:bg-n-ruby-2 hover:text-n-ruby-11 transition-colors focus:outline-none focus:ring-2 focus:ring-n-ruby-6"
              @click="confirmDelete(professional)"
            >
              <span class="i-lucide-trash-2 size-3.5" aria-hidden="true" />
              {{ t('APPOINTMENTS.TABLE.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals (Preserved forms) -->
    <ProfessionalForm
      v-if="showForm"
      :professional="selectedProfessional"
      :is-submitting="uiFlags.isCreating || uiFlags.isUpdating"
      :server-error="formError"
      @close="closeForm"
      @submit="handleFormSubmit"
    />

    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/60 backdrop-blur-sm p-4"
      >
        <div
          class="bg-n-background rounded-[1.25rem] shadow-2xl w-full max-w-sm p-6 ring-1 ring-white/10"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2 tracking-tight">
            {{ t('APPOINTMENTS.PROFESSIONALS.DELETE_CONFIRM_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-5">
            {{ t('APPOINTMENTS.PROFESSIONALS.DELETE_CONFIRM') }}
          </p>
          <div class="flex justify-end gap-3">
            <button
              type="button"
              class="px-5 py-2.5 text-sm font-semibold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-n-slate-6"
              @click="closeDeleteConfirm()"
            >
              {{ t('APPOINTMENTS.PROFESSIONALS.FORM.CANCEL') }}
            </button>
            <button
              type="button"
              :disabled="uiFlags.isDeleting"
              class="px-5 py-2.5 text-sm font-semibold text-white bg-n-ruby-9 hover:bg-n-ruby-9/90 rounded-xl disabled:opacity-50 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-n-ruby-9"
              @click="handleDelete"
            >
              <span
                v-if="uiFlags.isDeleting"
                class="i-lucide-loader-2 size-4 animate-spin inline-block mr-1"
              />
              {{ t('APPOINTMENTS.TABLE.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
